import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import {
  createConversationTipoAssistantSession,
  chatConversationTipoAssistant,
} from "../../api/client";

/**
 * "Tirar dúvidas" — fontes de conhecimento do tipo/subtipo (cascata) + contexto da solicitação.
 * @param {"default"|"dialog"} variant — `dialog` preenche altura disponível (ex.: popup de materiais).
 * @param {boolean} showPathInAlert — ocultar caminho no alerta quando o pai já exibe no título.
 */
export default function ConversationTypeAssistant({
  conversationId,
  variant = "default",
  showPathInAlert = true,
}) {
  const isDialog = variant === "dialog";
  const [sessionId, setSessionId] = useState(null);
  const [typePath, setTypePath] = useState([]);
  const [sessionError, setSessionError] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!conversationId) return;
    setSessionId(null);
    setMessages([]);
    setSessionError(null);
    setChatError(null);
    setSessionLoading(true);

    createConversationTipoAssistantSession(conversationId)
      .then((data) => {
        setSessionId(data?.sessionId || null);
        setTypePath(Array.isArray(data?.typePath) ? data.typePath : []);
      })
      .catch((err) => {
        setSessionError(err?.message || "Não foi possível iniciar o assistente.");
      })
      .finally(() => setSessionLoading(false));
  }, [conversationId]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, chatLoading]);

  const handleSend = async () => {
    const text = (input || "").trim();
    if (!text || !sessionId || !conversationId || chatLoading) return;

    const userMsg = { role: "user", content: text };
    const nextHistory = [...messages, userMsg];
    setMessages(nextHistory);
    setInput("");
    setChatError(null);
    setChatLoading(true);

    try {
      const data = await chatConversationTipoAssistant(conversationId, {
        sessionId,
        userMessage: text,
        history: messages,
      });
      const reply = data?.reply || data?.message || "Sem resposta.";
      setMessages([...nextHistory, { role: "assistant", content: reply }]);
    } catch (err) {
      setChatError(err?.message || "Erro ao enviar pergunta.");
      setMessages(messages);
    } finally {
      setChatLoading(false);
    }
  };

  const pathLabel = typePath.length ? typePath.join(" → ") : "tipo/subtipo";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minHeight: isDialog ? 0 : 200,
        flex: isDialog ? 1 : undefined,
        height: isDialog ? "100%" : undefined,
      }}
    >
      <Alert severity="info" variant="outlined" sx={{ py: 0.75, flexShrink: 0 }}>
        Respostas para você (atendente)
        {showPathInAlert ? (
          <>
            , com base nas fontes do tipo/subtipo (<strong>{pathLabel}</strong>)
          </>
        ) : null}{" "}
        e no contexto da solicitação — não são mensagens prontas para o cliente. Pode errar; confira documentos e
        políticas oficiais.
      </Alert>

      {sessionLoading && (
        <Stack direction="row" alignItems="center" spacing={1} color="text.secondary">
          <CircularProgress size={18} />
          <Typography variant="body2">Preparando assistente…</Typography>
        </Stack>
      )}

      {sessionError && <Alert severity="warning">{sessionError}</Alert>}

      {chatError && (
        <Alert severity="error" onClose={() => setChatError(null)}>
          {chatError}
        </Alert>
      )}

      <Box
        ref={listRef}
        sx={{
          flex: isDialog ? 1 : undefined,
          minHeight: isDialog ? 120 : 140,
          maxHeight: isDialog ? "none" : 280,
          overflowY: "auto",
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          p: 1.5,
          bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50"),
        }}
      >
        {messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Tire dúvidas para orientar o atendimento. Ex.: &quot;Quais são os procedimentos que o cliente deve seguir?&quot;
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {messages.map((m, idx) => (
              <Box
                key={idx}
                sx={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "94%" }}
              >
                <Box
                  sx={{
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: m.role === "user" ? "primary.main" : "background.paper",
                    color: m.role === "user" ? "primary.contrastText" : "text.primary",
                    border: m.role === "user" ? 0 : 1,
                    borderColor: "divider",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  <Typography variant="body2">{m.content}</Typography>
                </Box>
              </Box>
            ))}
            {chatLoading && (
              <Stack direction="row" alignItems="center" spacing={1} color="text.secondary">
                <CircularProgress size={16} />
                <Typography variant="body2">Pensando…</Typography>
              </Stack>
            )}
          </Stack>
        )}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
        <TextField
          size="small"
          fullWidth
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escreva sua dúvida…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={!sessionId || sessionLoading || chatLoading}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!input.trim() || !sessionId || chatLoading || sessionLoading}
          aria-label="Enviar"
        >
          <SendIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}
